import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import StillButton from './ui/StillButton';
import { useOverlay } from '../App';
import { useEffect, useState } from 'react';

const AboutOverlay = () => {
    const navigate = useNavigate();
    const { toggleOverlay } = useOverlay();
    const [isMobile, setIsMobile] = useState(false);

     // Check for mobile view
     useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 800);
        };
        
        // Check immediately
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Explicitly hide the header when this component mounts
    useEffect(() => {
        toggleOverlay(false);
        
        // Ensure header remains hidden when component unmounts
        return () => toggleOverlay(false);
    }, [toggleOverlay]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 1,
                staggerChildren: 0.5,
            },
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 5 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                duration: 2,
                ease: "easeOut"
            }
        }
    };

    const handleClose = () => {
        navigate('/space');
    };

    return (
        <div className="fixed inset-0 flex flex-col bg-black bg-opacity-40 overflow-y-auto">
            <motion.div 
                className="min-h-screen w-full flex flex-col items-center mt-20 p-4 lg:mt-0 lg:p-8"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <motion.div 
                    className="w-full lg:flex-1 flex flex-col justify-center px-4 lg:p-10 max-w-7xl"
                    variants={containerVariants}
                >
                    <motion.div 
                        className="space-y-6 pb-60 lg:pb-10"
                        variants={itemVariants}
                    >
                        <div className="flex flex-col items-center">
                            <h1 className="text-center max-w-[600px] mt-2">
                                <span className="text-white text-glow-still">HELPING FAMILIES HEAL</span><br/> BY BREAKING THE SILENCE <br/>ON EARLY BABY LOSS
                            </h1>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-still-white text-base lg:text-base max-w-[800px] mx-auto">
                            <motion.p variants={itemVariants}>
                            Every 3 seconds, a baby is lost before it's even born. This profoundly traumatic loss touches families worldwide, yet it remains a silent, neglected issue. For mothers, the grief is staggering, leaving them four times more likely to suffer from depression, seven times more prone to post-traumatic stress disorder, and at five times higher risk of suicide.
                            </motion.p>

                            <motion.p variants={itemVariants}>
                            Early baby loss is not just the loss of a child - it is the loss of dreams, hopes, and a future imagined. The emotional toll is compounded by societal silence, leaving families feeling lost and alone in an intangible grief.<br /><br />The Still Loved initiative was created to break this silence and offer a space of solace and remembrance for grieving families.
                            </motion.p>

                            <motion.p variants={itemVariants}>
                            Here each star tells a story of life, loss and endless love.<br /><br />Whether you're here to honour your baby, or feel connected to a global community, Still Loved is a space where love shines on, and no parent grieves alone. Because talking and remembering is often the healthiest way to heal.  

                            </motion.p>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>
            
            {/* Close button - fixed at bottom with proper spacing */}
            <motion.div 
                className={`fixed bottom-0 left-0 right-0 flex justify-center space-x-4 pointer-events-auto ${isMobile ? 'mb-0' : 'mb-12 sm:mb-0 md:mb-0 lg:mb-0'} z-50 pt-28 pb-20 py-4 bg-gradient-to-t from-black via-black to-transparent h-22`}
                variants={itemVariants}
            >
                <StillButton
                    onClick={handleClose}
                    aria-label="Close"
                    className="w-auto mb-0"
                >
                    CLOSE
                </StillButton>
            </motion.div>
        </div>
    );
};

export default AboutOverlay; 